import { EntityLinks } from '@griply/types';
import { getBlockedDropTargetIds, getTimelineColumns } from '@griply/utils';
import {
  addYears,
  differenceInDays,
  endOfQuarter,
  endOfYear,
  getDaysInMonth,
  getDaysInYear,
  startOfQuarter,
  startOfYear,
} from 'date-fns';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { GoalItemCard } from 'shared/components/ui/goal-item-card';
import { useToday } from 'shared/contexts/today';
import { DateRange } from 'shared/types/date-range';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import {
  TimelineCategory,
  TimelineDropSlot,
  TimelineScrollRequest,
  TimelineSubCategory,
  TimelineTimeframes,
} from 'shared/types/timeline';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { useTheme } from 'styled-components';

import { buildTimelineRowTops } from './build-timeline-row-tops';
import { CategoryListItem } from './category-list-item';
import {
  computeTimelineDropDates,
  getTimelineDropDurationDays,
} from './compute-timeline-drop-dates';
import { dropLineIndentSpacing, rowHeight } from './constants';
import { getTimelineMonthDividers } from './get-timeline-month-dividers';
import { getTimelineScrollLeft } from './get-timeline-scroll-left';
import { getTimelineWeekendBands } from './get-timeline-weekend-bands';
import { GoalBar } from './goal-bar';
import * as Styled from './goal-timeline.style';
import { Placeholder } from './placeholder';
import {
  isSameTimelineDropSlot,
  resolveSidebarDropSlot,
} from './resolve-sidebar-drop-slot';
import { SidebarDropIndicator } from './sidebar-drop-indicator';
import { TimelineEdgeArrows } from './timeline-edge-arrows';
import { TimelineGridBackground } from './timeline-grid-background';
import { TimelineHeaderColumns } from './timeline-header-columns';
import { TodayLine } from './today-line';
import {
  getTimelineDragSource,
  PositionedTimelineColumn,
  SideBarItem,
  SideBarItemGoal,
  TimelineDragSource,
  TimelineDropAnchors,
} from './types';

type PlaceholderState = {
  visible: boolean;
  dayIndex: number;
  goalId: ID | null;
};

export type GoalTimelineProps = {
  categories: TimelineCategory[];
  goals: Goal[];
  // Migrated parent links: the drop guard needs them to see the dragged goal's descendants.
  links: EntityLinks;
  lifeAreas: LifeArea[];
  dateRange: DateRange;
  onUpdateDates: (
    goalId: ID,
    newStartDate: Timestamp,
    newEndDate: Timestamp,
  ) => void;
  onReorderGoals?: (
    slot: TimelineDropSlot,
    dragged: TimelineDragSource,
  ) => void;
  collapsedItems: ID[];
  toggleCollapse: (id: ID) => void;
  onCategoryAdd?: (id: ID, anchor: HTMLElement) => void;
  timeframe: TimelineTimeframes;
  referenceDate: Date;
  scrollRequest: TimelineScrollRequest;
  weekStartsOn: WeekDays;
  title: string;
};

const timeframeDaysFuncMap: Record<
  TimelineTimeframes,
  (date: Timestamp) => number
> = {
  [TimelineTimeframes.Month]: getDaysInMonth,
  [TimelineTimeframes.Quarter]: (date) =>
    differenceInDays(endOfQuarter(date), startOfQuarter(date)),
  [TimelineTimeframes.Year]: getDaysInYear,
  [TimelineTimeframes.FiveYears]: (date) =>
    differenceInDays(endOfYear(addYears(date, 4)), startOfYear(date)),
};

// Helper to convert rem string to px number
const remToPx = (remString: string) => parseFloat(remString) * 10;

// Lazy singleton (jsdom has no canvas) — preloaded at mount because
// setDragImage silently falls back to the native snapshot for an image that
// isn't fully loaded yet.
let transparentDragImage: HTMLImageElement | null = null;
const getTransparentDragImage = () => {
  if (!transparentDragImage) {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    transparentDragImage = new Image();
    transparentDragImage.src = canvas.toDataURL();
  }
  return transparentDragImage;
};

type TimelineGridRowProps = {
  item: SideBarItemGoal;
  top: number;
  goals: Goal[];
  links: EntityLinks;
  lifeAreas: LifeArea[];
  dateRange: DateRange;
  dayWidth: number;
  timeframe: TimelineTimeframes;
  onUpdateDates: (
    goalId: ID,
    newStartDate: Timestamp,
    newEndDate: Timestamp,
  ) => void;
  placeholderDayIndex: number | null;
  previewDayIndex: number | null;
  previewDurationDays: number | null;
};

const TimelineGridRow: React.FC<TimelineGridRowProps> = React.memo(
  ({
    item,
    top,
    goals,
    links,
    lifeAreas,
    dateRange,
    dayWidth,
    timeframe,
    onUpdateDates,
    placeholderDayIndex,
    previewDayIndex,
    previewDurationDays,
  }) => {
    const { goal } = item;
    return (
      <Styled.GoalRow $top={top}>
        <Styled.GoalRowInner>
          {goal.startDate && goal.deadline && (
            <GoalBar
              goal={goal}
              goals={goals}
              links={links}
              lifeAreas={lifeAreas}
              dateRange={dateRange}
              dayWidth={dayWidth}
              onUpdateDates={onUpdateDates}
            />
          )}

          {(!goal.startDate || !goal.deadline) &&
            placeholderDayIndex !== null && (
              <Placeholder
                dayWidth={dayWidth}
                dayIndex={placeholderDayIndex}
                durationDays={getTimelineDropDurationDays(goal, timeframe)}
              />
            )}

          {previewDayIndex !== null && previewDurationDays !== null && (
            <Placeholder
              dayWidth={dayWidth}
              dayIndex={previewDayIndex}
              durationDays={previewDurationDays}
              anchor={TimelineDropAnchors.StartAtCursor}
            />
          )}
        </Styled.GoalRowInner>
      </Styled.GoalRow>
    );
  },
);

const GoalTimelineComponent = React.forwardRef<
  HTMLDivElement,
  GoalTimelineProps
>(
  (
    {
      categories,
      goals,
      links,
      lifeAreas,
      dateRange,
      onUpdateDates,
      onReorderGoals,
      collapsedItems,
      toggleCollapse,
      onCategoryAdd,
      timeframe,
      referenceDate,
      scrollRequest,
      weekStartsOn,
      title,
    },
    ref,
  ) => {
    const [placeholderState, setPlaceholderState] = useState<PlaceholderState>({
      visible: false,
      dayIndex: 0,
      goalId: null,
    });
    const [draggedRow, setDraggedRow] = useState<SideBarItemGoal | null>(null);
    const draggedGoalId = draggedRow?.id ?? null;
    const [activeSlot, setActiveSlot] = useState<TimelineDropSlot | null>(null);
    const [canvasPreviewDayIndex, setCanvasPreviewDayIndex] = useState<
      number | null
    >(null);
    const [containerWidth, setContainerWidth] = useState(0);

    const blockedIdsRef = useRef<Set<ID>>(new Set());
    const categoryListRef = useRef<HTMLDivElement>(null);
    const dragPreviewRef = useRef<HTMLDivElement>(null);
    const leftBodyRef = useRef<HTMLDivElement>(null);
    const rightHeaderRef = useRef<HTMLDivElement>(null);
    const timelineBodyRef = useRef<HTMLDivElement | null>(null);

    // Populate both the forwarded ref (container's scroll controls) and a local
    // ref the edge-arrows overlay subscribes to for horizontal scroll.
    const setTimelineBodyRef = useCallback(
      (node: HTMLDivElement | null) => {
        timelineBodyRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
          return;
        }
        if (ref) {
          (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }
      },
      [ref],
    );

    const theme = useTheme();
    const today = useToday();

    const { totalDays, dayWidth } = useMemo(() => {
      const totalDays = differenceInDays(
        dateRange.endDate,
        dateRange.startDate,
      );
      const daysInView = timeframeDaysFuncMap[timeframe](referenceDate);
      const calculatedDayWidth =
        containerWidth > 0
          ? Math.floor((containerWidth / daysInView) * 100) / 100
          : 0;

      return { totalDays, dayWidth: calculatedDayWidth };
    }, [
      containerWidth,
      dateRange.endDate,
      dateRange.startDate,
      referenceDate,
      timeframe,
    ]);

    const timelineGridWidth = totalDays * dayWidth;

    const targetScrollLeft = useMemo(
      () =>
        getTimelineScrollLeft({
          anchor: scrollRequest.anchor,
          referenceDate,
          today,
          rangeStartDate: dateRange.startDate,
          dayWidth,
          viewportWidth: containerWidth,
        }),
      [
        scrollRequest.anchor,
        referenceDate,
        today,
        dateRange.startDate,
        dayWidth,
        containerWidth,
      ],
    );

    const handleScroll = () => {
      if (!ref || typeof ref === 'function' || !ref.current) {
        return;
      }
      const { scrollTop, scrollLeft } = ref.current;
      if (leftBodyRef.current) {
        leftBodyRef.current.scrollTop = scrollTop;
      }
      if (rightHeaderRef.current) {
        rightHeaderRef.current.scrollLeft = scrollLeft;
      }
    };

    const flattenedRows = useMemo(() => {
      const result: SideBarItem[] = [];
      categories.forEach((category) => {
        if (!category.hideHeader) {
          result.push({
            id: category.id,
            rowKey: category.id,
            categoryId: category.id,
            lifeArea: category.lifeArea,
            hasGoals: !!category.subCategories.length,
            level: 0,
          });
        }

        if (category.hideHeader || !collapsedItems.includes(category.id)) {
          const flatten = (
            subCategories: TimelineSubCategory[],
            level: number,
            pathPrefix: string,
          ) => {
            subCategories.forEach((subCategory) => {
              const goal = subCategory.goal;
              const rowKey = `${pathPrefix}/${goal.id}`;

              result.push({
                id: goal.id,
                rowKey,
                categoryId: category.id,
                level,
                goal,
                hasGoals: !!subCategory.subCategories.length,
              });

              if (!collapsedItems.includes(goal.id)) {
                flatten(subCategory.subCategories, level + 1, rowKey);
              }
            });
          };

          flatten(category.subCategories, 0, category.id);
        }
      });
      return result;
    }, [categories, collapsedItems]);

    const categoryGap = remToPx(theme.spacing(4));

    // Row tops per pane: the canvas grid has spacing(2.5) top padding, the
    // sidebar list spacing(2) — same series, different offset.
    const rowTops = useMemo(
      () =>
        buildTimelineRowTops(flattenedRows, {
          topOffset: remToPx(theme.spacing(2.5)),
          categoryGap,
        }),
      [flattenedRows, theme, categoryGap],
    );

    const sidebarRowTops = useMemo(
      () =>
        buildTimelineRowTops(flattenedRows, {
          topOffset: remToPx(theme.spacing(2)),
          categoryGap,
        }),
      [flattenedRows, theme, categoryGap],
    );

    // Calculate the total height of all rows (including extra category spacing)
    const totalRowsHeight = useMemo(() => {
      if (rowTops.length === 0) {
        return 0;
      }
      return rowTops[rowTops.length - 1] + rowHeight;
    }, [rowTops]);

    const draggedGoal = useMemo(
      () => goals.find(({ id }) => id === draggedGoalId) ?? null,
      [goals, draggedGoalId],
    );

    const moveDragPreviewTo = useCallback(
      (clientX: number, clientY: number) => {
        if (!dragPreviewRef.current) {
          return;
        }
        dragPreviewRef.current.style.transform = `translate3d(${clientX + 12}px, ${clientY + 12}px, 0)`;
        dragPreviewRef.current.style.visibility = 'visible';
      },
      [],
    );

    const hideDragPreview = useCallback(() => {
      if (dragPreviewRef.current) {
        dragPreviewRef.current.style.visibility = 'hidden';
      }
    }, []);

    const clearDragState = useCallback(() => {
      blockedIdsRef.current = new Set();
      hideDragPreview();
      setDraggedRow(null);
      setActiveSlot(null);
      setCanvasPreviewDayIndex(null);
    }, [hideDragPreview]);

    const handleRowDragStart = useCallback(
      (e: React.DragEvent, row: SideBarItemGoal) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', row.id);
        e.dataTransfer.setDragImage(getTransparentDragImage(), 0, 0);
        moveDragPreviewTo(e.clientX, e.clientY);
        blockedIdsRef.current = getBlockedDropTargetIds(row.id, goals, links);
        setDraggedRow(row);
      },
      [goals, links, moveDragPreviewTo],
    );

    const handleRowDragEnd = useCallback(
      () => clearDragState(),
      [clearDragState],
    );

    const handleSidebarDragOver = (e: React.DragEvent) => {
      if (!draggedGoalId || !categoryListRef.current) {
        return;
      }
      const rect = categoryListRef.current.getBoundingClientRect();
      const slot = resolveSidebarDropSlot({
        rows: flattenedRows,
        rowTops: sidebarRowTops,
        cursorX: e.clientX - rect.left,
        cursorY: e.clientY - rect.top,
        draggedGoalId,
        blockedIds: blockedIdsRef.current,
        indentBase: remToPx(theme.spacing(2.5)),
        indentWidth: remToPx(theme.spacing(dropLineIndentSpacing)),
      });
      if (slot) {
        e.preventDefault();
      }
      moveDragPreviewTo(e.clientX, e.clientY);
      setCanvasPreviewDayIndex(null);
      setActiveSlot((prev) =>
        isSameTimelineDropSlot(prev, slot) ? prev : slot,
      );
    };

    const handleSidebarDragLeave = (e: React.DragEvent) => {
      if (e.currentTarget.contains(e.relatedTarget as Node)) {
        return;
      }
      hideDragPreview();
      setActiveSlot(null);
    };

    const handleSidebarDrop = (e: React.DragEvent) => {
      e.preventDefault();
      if (draggedRow && activeSlot) {
        onReorderGoals?.(activeSlot, getTimelineDragSource(draggedRow));
      }
      clearDragState();
    };

    const getDayIndexFromClientX = (clientX: number) => {
      if (!ref || typeof ref === 'function' || !ref.current || dayWidth <= 0) {
        return null;
      }
      const timelineRect = ref.current.getBoundingClientRect();
      const x = clientX - timelineRect.left + ref.current.scrollLeft;
      return Math.round(x / dayWidth);
    };

    const handleCanvasDragOver = (e: React.DragEvent) => {
      if (!draggedGoalId) {
        return;
      }
      const dayIndex = getDayIndexFromClientX(e.clientX);
      if (dayIndex === null) {
        return;
      }
      e.preventDefault();
      hideDragPreview();
      setActiveSlot(null);
      setCanvasPreviewDayIndex((prev) => (prev === dayIndex ? prev : dayIndex));
    };

    const handleCanvasDragLeave = (e: React.DragEvent) => {
      if (e.currentTarget.contains(e.relatedTarget as Node)) {
        return;
      }
      setCanvasPreviewDayIndex(null);
    };

    const handleCanvasDrop = (e: React.DragEvent) => {
      e.preventDefault();
      const dayIndex = getDayIndexFromClientX(e.clientX);
      if (draggedGoal && dayIndex !== null) {
        const { startDate, deadline } = computeTimelineDropDates({
          goal: draggedGoal,
          dayIndex,
          rangeStartDate: dateRange.startDate,
          timeframe,
          anchor: TimelineDropAnchors.StartAtCursor,
        });
        onUpdateDates(draggedGoal.id, startDate, deadline);
      }
      clearDragState();
    };

    const handleTimelineMouseMove = (e: React.MouseEvent) => {
      if (!ref || typeof ref === 'function' || !ref.current || dayWidth <= 0) {
        return;
      }

      const timelineRect = ref.current.getBoundingClientRect();
      const mouseY = e.clientY - timelineRect.top + ref.current.scrollTop;

      // We iterate through all possible rows to find which one the mouse is hovering over.
      // We can't use a simple division by rowHeight, because categories have extra vertical spacing.
      const rowIndex = rowTops.findIndex((rowTop) => {
        const rowBottom = rowTop + rowHeight;
        return mouseY >= rowTop && mouseY <= rowBottom;
      });

      // If no row is hovered, hide the placeholder.
      if (rowIndex === -1) {
        setPlaceholderState({ visible: false, dayIndex: 0, goalId: null });
        return;
      }

      const item = flattenedRows[rowIndex];

      if (
        item &&
        'goal' in item &&
        (!item.goal.startDate || !item.goal.deadline)
      ) {
        const dayIndex = getDayIndexFromClientX(e.clientX);
        if (dayIndex === null) {
          return;
        }
        setPlaceholderState({
          visible: true,
          dayIndex,
          goalId: item.id,
        });
        return;
      }

      setPlaceholderState({ visible: false, dayIndex: 0, goalId: null });
    };

    const handleTimelineMouseLeave = () =>
      setPlaceholderState({ visible: false, dayIndex: 0, goalId: null });

    const handleTimelineClick = () => {
      if (!placeholderState.visible || !placeholderState.goalId) {
        return;
      }
      const goal = goals.find(({ id }) => id === placeholderState.goalId);
      if (!goal) {
        return;
      }

      const { startDate, deadline } = computeTimelineDropDates({
        goal,
        dayIndex: placeholderState.dayIndex,
        rangeStartDate: dateRange.startDate,
        timeframe,
        anchor: TimelineDropAnchors.Centered,
      });
      onUpdateDates(placeholderState.goalId, startDate, deadline);
    };

    const columns = useMemo(
      () =>
        getTimelineColumns({ timeframe, dateRange, weekStartsOn, dayWidth }),
      [dateRange, timeframe, weekStartsOn, dayWidth],
    );

    // Absolute x-offset per column, so the header + background can virtualize by
    // it (render only the columns intersecting the visible window).
    const positionedColumns = useMemo<PositionedTimelineColumn[]>(() => {
      let offset = 0;
      return columns.map((column) => {
        const positioned = { ...column, left: offset };
        offset += column.width;
        return positioned;
      });
    }, [columns]);

    const weekendBands = useMemo(
      () =>
        [TimelineTimeframes.Month, TimelineTimeframes.Quarter].includes(
          timeframe,
        )
          ? getTimelineWeekendBands({ dateRange, dayWidth })
          : [],
      [timeframe, dateRange, dayWidth],
    );

    const monthDividers = useMemo(
      () =>
        timeframe === TimelineTimeframes.Month
          ? getTimelineMonthDividers({ dateRange, dayWidth })
          : [],
      [timeframe, dateRange, dayWidth],
    );

    useEffect(() => {
      getTransparentDragImage();
    }, []);

    useLayoutEffect(() => {
      if (!ref || typeof ref === 'function' || !ref.current) {
        return;
      }
      const element = ref.current;
      // Measure synchronously before the first paint so the header + grid render
      // at real column widths, instead of collapsing to 0 (all columns stacked)
      // for a frame until the ResizeObserver fires.
      setContainerWidth(element.getBoundingClientRect().width);
      const resizeObserver = new ResizeObserver((entries) => {
        if (entries[0]) {
          setContainerWidth(entries[0].contentRect.width);
        }
      });
      resizeObserver.observe(element);
      return () => resizeObserver.disconnect();
    }, [ref]);

    // Pre-paint so a navigation lands in one frame; the windowed overlays get targetScrollLeft too (useTimelineScrollLeft).
    // scrollRequest.id re-runs this for an unchanged target (today after a manual scroll-away).
    useLayoutEffect(() => {
      if (!ref || typeof ref === 'function' || !ref.current || dayWidth <= 0) {
        return;
      }
      ref.current.scrollTo({ left: targetScrollLeft, behavior: 'auto' });
    }, [ref, dayWidth, targetScrollLeft, scrollRequest.id]);

    return (
      <Styled.Container>
        <Styled.SidebarDragPreview ref={dragPreviewRef}>
          {!!draggedGoal && (
            <Styled.SidebarDragPreviewCard>
              <GoalItemCard
                goal={draggedGoal}
                lifeAreas={lifeAreas}
                links={links}
                goals={goals}
                deadlineOnly
                showTypeIcon
              />
            </Styled.SidebarDragPreviewCard>
          )}
        </Styled.SidebarDragPreview>

        <Styled.HeaderRow>
          <Styled.GoalListHeader>
            <Styled.GoalListTitle>{title}</Styled.GoalListTitle>
          </Styled.GoalListHeader>
          <Styled.TimelineHeader ref={rightHeaderRef}>
            <Styled.TimelineHeaderContent
              $timelineGridWidth={timelineGridWidth}
            >
              <TimelineHeaderColumns
                columns={positionedColumns}
                scrollRef={timelineBodyRef}
                viewportWidth={containerWidth}
                syncTo={targetScrollLeft}
              />
            </Styled.TimelineHeaderContent>
          </Styled.TimelineHeader>
        </Styled.HeaderRow>

        <Styled.BodyContainer>
          <Styled.Body>
            <Styled.GoalListBody ref={leftBodyRef}>
              <Styled.CategoryList
                ref={categoryListRef}
                $height={totalRowsHeight}
                onDragOver={handleSidebarDragOver}
                onDrop={handleSidebarDrop}
                onDragLeave={handleSidebarDragLeave}
              >
                {flattenedRows.map((item) => (
                  <CategoryListItem
                    key={item.rowKey}
                    item={item}
                    goals={goals}
                    links={links}
                    lifeAreas={lifeAreas}
                    isDragged={draggedRow?.rowKey === item.rowKey}
                    isDraggable={!!onReorderGoals}
                    onDragStart={handleRowDragStart}
                    onDragEnd={handleRowDragEnd}
                    toggleCollapse={toggleCollapse}
                    collapsedItems={collapsedItems}
                    onCategoryAdd={onCategoryAdd}
                  />
                ))}
                {!!activeSlot && <SidebarDropIndicator slot={activeSlot} />}
              </Styled.CategoryList>
            </Styled.GoalListBody>

            <Styled.TimelineViewport>
              <Styled.TimelineBody
                ref={setTimelineBodyRef}
                onScroll={handleScroll}
                onMouseMove={handleTimelineMouseMove}
                onMouseLeave={handleTimelineMouseLeave}
                onClick={handleTimelineClick}
                onDragOver={handleCanvasDragOver}
                onDrop={handleCanvasDrop}
                onDragLeave={handleCanvasDragLeave}
              >
                <Styled.TimelineGrid
                  $timelineGridWidth={timelineGridWidth}
                  $height={totalRowsHeight}
                >
                  <TimelineGridBackground
                    columns={positionedColumns}
                    hideColumnBorders={timeframe === TimelineTimeframes.Month}
                    weekendBands={weekendBands}
                    monthDividers={monthDividers}
                    scrollRef={timelineBodyRef}
                    viewportWidth={containerWidth}
                    syncTo={targetScrollLeft}
                  />

                  <TodayLine dateRange={dateRange} dayWidth={dayWidth} />

                  {flattenedRows.map((item, index) => {
                    if (!('goal' in item)) {
                      return null;
                    }
                    const placeholderDayIndex =
                      placeholderState.visible &&
                      placeholderState.goalId === item.id
                        ? placeholderState.dayIndex
                        : null;
                    const isPreviewRow =
                      draggedGoal?.id === item.id &&
                      canvasPreviewDayIndex !== null;
                    return (
                      <TimelineGridRow
                        key={item.rowKey}
                        item={item}
                        top={rowTops[index]}
                        goals={goals}
                        links={links}
                        lifeAreas={lifeAreas}
                        dateRange={dateRange}
                        dayWidth={dayWidth}
                        timeframe={timeframe}
                        onUpdateDates={onUpdateDates}
                        placeholderDayIndex={placeholderDayIndex}
                        previewDayIndex={
                          isPreviewRow ? canvasPreviewDayIndex : null
                        }
                        previewDurationDays={
                          isPreviewRow && draggedGoal
                            ? getTimelineDropDurationDays(
                                draggedGoal,
                                timeframe,
                              )
                            : null
                        }
                      />
                    );
                  })}
                </Styled.TimelineGrid>
              </Styled.TimelineBody>

              <TimelineEdgeArrows
                rows={flattenedRows}
                rowTops={rowTops}
                goals={goals}
                lifeAreas={lifeAreas}
                links={links}
                dateRange={dateRange}
                dayWidth={dayWidth}
                viewportWidth={containerWidth}
                scrollRef={timelineBodyRef}
                syncTo={targetScrollLeft}
              />
            </Styled.TimelineViewport>
          </Styled.Body>
        </Styled.BodyContainer>
      </Styled.Container>
    );
  },
);

GoalTimelineComponent.displayName = 'GoalTimeline';

export const GoalTimeline = React.memo(GoalTimelineComponent);
